import { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faBookSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { useUpdateOnboardingTourProgressMutation } from "@/pages/Private/redux/onboardingTour/onboardingTour.api";
import {
	markStepCompletedLocal,
	openTour,
	selectTourState,
	setCurrentStepLocal,
	setKbHandoffActive,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { STEP_ORDER } from "@/pages/Private/pages/OnboardingTour/types";
import { TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

/** Query flag set by the onboarding tour when it hands the user over to this page. */
export const KNOWLEDGE_DB_TOUR_PARAM = "tour";

/**
 * Sticky bottom bar shown while the Knowledge DB page is visited as part of the
 * onboarding tour (`?tour=1`, PREC-3719 §4.4). The Knowledge Base is now reviewed
 * on the real page rather than in the modal; "Looks good" marks the KNOWLEDGE
 * step complete, advances to the next step, and reopens the tour there. While the
 * bar is mounted the handoff flag is set so that leaving via the left nav reopens
 * the configuration modal instead of silently dropping out of the tour (PREC-3745).
 */
export const KnowledgeDbTourReturnBar: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const tourState = useAppSelector(selectTourState);

	const [updateProgress] = useUpdateOnboardingTourProgressMutation();

	const isTourVisit = searchParams.get(KNOWLEDGE_DB_TOUR_PARAM) === "1";

	useEffect(() => {
		if (isTourVisit) {
			dispatch(setKbHandoffActive(true));
		}
	}, [isTourVisit, dispatch]);

	if (!isTourVisit) {
		return null;
	}

	const looksGood = () => {
		const knowledgeIndex = STEP_ORDER.indexOf(TourStepId.KNOWLEDGE);
		// The Knowledge Base is built in the background now, so KNOWLEDGE is no
		// longer part of the flow: an unlisted step has no successor to advance to.
		const nextStep = knowledgeIndex === -1 ? null : (STEP_ORDER[knowledgeIndex + 1] ?? null);

		const completedSteps = Array.from(
			new Set([...(tourState?.completedSteps ?? []), TourStepId.KNOWLEDGE])
		);

		dispatch(markStepCompletedLocal(TourStepId.KNOWLEDGE));

		if (nextStep) {
			dispatch(setCurrentStepLocal(nextStep));
		}

		void updateProgress({ completedSteps, ...(nextStep ? { currentStep: nextStep } : {}) });

		if (nextStep) {
			dispatch(openTour(nextStep));
		}

		navigate(ROUTE_CONFIG.DASHBOARD);
	};

	return (
		<div
			className="sticky bottom-0 z-20 flex flex-wrap items-center gap-4 border-t-2 border-brand-light bg-blue-action-200 px-4 py-3 shadow-all"
			data-testid="kb-tour-return-bar"
		>
			<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-light text-white">
				<Icon className="h-5 w-5" icon={faBookSparkles} />
			</span>
			<div className="flex min-w-[200px] flex-1 flex-col">
				<span className="text-sm font-bold text-text">
					{t("onboardingTour.reviewSteps.knowledge.returnBar.title")}
				</span>
				<span className="text-ssm text-text-meta">
					{t("onboardingTour.reviewSteps.knowledge.returnBar.text")}
				</span>
			</div>
			<Button
				size={ButtonSize.M}
				title={t("onboardingTour.reviewSteps.knowledge.returnBar.cta")}
				type="button"
				onClick={looksGood}
			/>
		</div>
	);
};
